import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from 'src/common/utils/api';
import { ApiError } from 'src/error/ApiError';
import { invalidateDataUploadFiles } from 'src/data-capture/hooks/file-uploads/invalidateDataUploadFiles';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SaveOwner } from './PresentedFileCard';

type SavePresentedFileInput = {
  conversationId: string;
  url: string;
  // Only used to refresh the right caches and word the notification. The server files the
  // document against the conversation's own client or family, never against this.
  owner: SaveOwner;
  parentId: string | null;
};

const ownerIds = (owner: SaveOwner) =>
  owner.kind === 'client' ? { clientId: owner.id } : { familyId: owner.id };

export function useSavePresentedFile() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<unknown, Error, SavePresentedFileInput>({
    mutationFn: ({ conversationId, url, parentId }) =>
      api.post(
        `conversations/${conversationId}/presented-files/save`,
        { url, parentId },
        { expectedStatuses: [410] },
      ),
    onSuccess: (_, { owner, conversationId }) => {
      invalidateDataUploadFiles(queryClient, ownerIds(owner));
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'presented-files', 'saves'],
      });
      notifications.show({
        title: owner.kind === 'client' ? 'Saved to client documents' : 'Saved to family documents',
        message:
          owner.kind === 'client'
            ? 'The file has been added to the client’s documents.'
            : 'The file has been added to the family’s documents.',
        color: 'green',
      });
    },
    onError: error => {
      // A 410 means Siesta's copy of the export has gone from S3, so retrying can never
      // work. Say so plainly instead of inviting a retry, and don't report it as a system
      // failure — nothing is broken.
      if (error instanceof ApiError && error.status === 410) {
        notifications.show({
          title: 'File no longer available',
          message: 'Ask Siesta to produce the file again, then save it.',
          color: 'red',
        });
        return;
      }
      notifyAndLogError('Could not save the file', 'Please try again.', error);
    },
  });

  const savePresentedFile = async (input: SavePresentedFileInput) => {
    await mutateAsync(input);
  };

  return { savePresentedFile, isSaving: isPending };
}

type ConversationSaveState = { owner: SaveOwner | null; sourceKeys: string[] };

/**
 * Where this conversation's files would be saved, and which are already there.
 *
 * The owner comes from the server rather than the page, because the two can disagree: an
 * advisor can open a client's chat and then navigate elsewhere, and the file still belongs
 * to the conversation. A conversation with no owner (a general chat) cannot save at all.
 */
export function useSavedPresentedFiles(conversationId: string | undefined, enabled: boolean) {
  const { data, isError } = useQuery<ConversationSaveState>({
    queryKey: ['conversations', conversationId, 'presented-files', 'saves'],
    enabled: enabled && !!conversationId,
    // Flag-gating (and not-yet-persisted conversations) mean a 404; never retry or surface it.
    retry: false,
  });
  if (isError) return { savedSourceKeys: [], saveOwner: undefined };
  return { savedSourceKeys: data?.sourceKeys ?? [], saveOwner: data?.owner ?? undefined };
}
